import { useEffect, useState } from "react";
import { useLocation } from "@/components/location-provider";
import { GlassPanel } from "@/components/ui/glass-panel";
import { PageHeader } from "@/components/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeftRight, Thermometer, Droplets, Wind, Eye, Sun as SunIcon } from "lucide-react";
import { useGeocode } from "@/lib/use-geocode";

function formatTemp(c: number) {
  return `${Math.round(c)}°`;
}

interface LocationData {
  weather: any;
  aqi: any;
}

export default function ComparePage() {
  const [loc2Query, setLoc2Query] = useState("");
  const [loc2, setLoc2] = useState<{ name: string; lat: number; lon: number; country: string } | null>(null);
  const [data1, setData1] = useState<LocationData | null>(null);
  const [data2, setData2] = useState<LocationData | null>(null);
  const [loading, setLoading] = useState(false);
  const { current: location } = useLocation();
  const { results: loc2Results } = useGeocode(loc2Query);

  useEffect(() => {
    fetch(`/api/weather?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => r.json())
      .then((d) => setData1({ weather: d.weather, aqi: d.aqi }))
      .catch(() => {});
  }, [location.lat, location.lon]);

  useEffect(() => {
    if (!loc2) return;
    setLoading(true);
    fetch(`/api/compare?lat1=${location.lat}&lon1=${location.lon}&lat2=${loc2.lat}&lon2=${loc2.lon}`)
      .then((r) => r.json())
      .then((d) => {
        setData1(d.location1);
        setData2(d.location2);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [loc2, location.lat, location.lon]);

  const handleSearch = (q: string) => {
    setLoc2Query(q);
  };

  const c1 = data1?.weather?.current;
  const d1 = data1?.weather?.daily?.[0];
  const c2 = data2?.weather?.current;
  const d2 = data2?.weather?.daily?.[0];

  const rows = [
    { label: "Temperature", icon: Thermometer, v1: c1 ? formatTemp(c1.temp) : "—", v2: c2 ? formatTemp(c2.temp) : "—", unit: "°C" },
    { label: "Feels Like", icon: Thermometer, v1: c1 ? formatTemp(c1.feels_like) : "—", v2: c2 ? formatTemp(c2.feels_like) : "—", unit: "°C" },
    { label: "High / Low", icon: Thermometer, v1: d1 ? `${formatTemp(d1.temp.max)}/${formatTemp(d1.temp.min)}` : "—", v2: d2 ? `${formatTemp(d2.temp.max)}/${formatTemp(d2.temp.min)}` : "—", unit: "" },
    { label: "Humidity", icon: Droplets, v1: c1 ? `${c1.humidity}%` : "—", v2: c2 ? `${c2.humidity}%` : "—", unit: "" },
    { label: "Wind", icon: Wind, v1: c1 ? `${Math.round(c1.wind_speed)} km/h` : "—", v2: c2 ? `${Math.round(c2.wind_speed)} km/h` : "—", unit: "" },
    { label: "Visibility", icon: Eye, v1: c1 ? `${(c1.visibility / 1000).toFixed(1)} km` : "—", v2: c2 ? `${(c2.visibility / 1000).toFixed(1)} km` : "—", unit: "" },
    { label: "UV Index", icon: SunIcon, v1: c1 ? c1.uvi?.toFixed(1) : "—", v2: c2 ? c2.uvi?.toFixed(1) : "—", unit: "" },
  ];

  return (
    <div className="space-y-5 animate-slide-up">
      <PageHeader eyebrow="Tools" title="Compare" subtitle={`${location.name} vs ${loc2?.name || "another city"}`} />

      <GlassPanel intensity="low" className="p-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search city to compare..."
            value={loc2Query}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full rounded-lg border border-border/40 bg-background/50 backdrop-blur-sm px-3 py-2 text-sm outline-none focus:border-primary/50 transition-colors"
          />
          {loc2Results.length > 0 && !loc2 && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-xl border border-border/40 bg-popover/95 backdrop-blur-xl shadow-xl max-h-48 overflow-y-auto">
              {loc2Results.map((r, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setLoc2({ name: r.name, lat: r.lat, lon: r.lon, country: r.country });
                    setLoc2Query(r.name);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-muted/50 transition-colors"
                >
                  {r.name}, {r.country}
                </button>
              ))}
            </div>
          )}
        </div>
      </GlassPanel>

      {loading ? (
        <div className="grid gap-4">
          <Skeleton className="h-40 rounded-2xl" />
        </div>
      ) : loc2 && data1 && data2 ? (
        <div className="space-y-3">
          <GlassPanel intensity="medium" className="p-4">
            <div className="flex items-center justify-between text-center">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">{location.name}</p>
                <p className="text-lg font-bold">{c1 ? formatTemp(c1.temp) : "—"}</p>
                <p className="text-xs text-muted-foreground">{c1?.weather?.[0]?.description || ""}</p>
              </div>
              <div className="flex items-center justify-center px-4">
                <ArrowLeftRight className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">{loc2.name}</p>
                <p className="text-lg font-bold">{c2 ? formatTemp(c2.temp) : "—"}</p>
                <p className="text-xs text-muted-foreground">{c2?.weather?.[0]?.description || ""}</p>
              </div>
            </div>
          </GlassPanel>

          {rows.map((row) => (
            <GlassPanel key={row.label} intensity="low" className="px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <row.icon className="h-3.5 w-3.5 text-primary/60" />
                  <span className="text-xs font-medium text-muted-foreground">{row.label}</span>
                </div>
                <div className="flex-1 text-center grid grid-cols-2">
                  <span className="text-sm font-semibold">{row.v1}</span>
                  <span className="text-sm font-semibold">{row.v2}</span>
                </div>
              </div>
            </GlassPanel>
          ))}
        </div>
      ) : (
        <GlassPanel intensity="low" className="p-12 text-center">
          <ArrowLeftRight className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h2 className="text-lg font-bold">Compare Weather</h2>
          <p className="text-sm text-muted-foreground mt-1">Search for a city above to compare side by side.</p>
        </GlassPanel>
      )}
    </div>
  );
}